import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {SharedFeaturesService} from 'src/app/services/shared-features.service';
import {UserAppService} from 'src/app/services/user-app.service';
import {delay} from 'rxjs/operators';
import {of} from 'rxjs';
import {OtpVerificationComponent} from '../otp-verification/otp-verification.component';
import {MatDialog} from '@angular/material/dialog';

@Component({
  selector: 'app-sign-up',
  template: `
    <div class="signup-card">
      <h2 class="signup-title">Create account</h2>
      <p class="signup-sub">Set up your workspace to get started.</p>

      <form
        [formGroup]="signup"
        (ngSubmit)="
          IsEmailVerificationRequired ? onSubmit() : onSubmitEmailNotRequired()
        "
        class="signup-form"
      >
        <div class="field">
          <label>First name</label>
          <mat-form-field class="signup-field">
            <input
              matInput
              placeholder="Enter your first name"
              formControlName="first_name"
              required
            />
          </mat-form-field>
        </div>

        <div class="field">
          <label>Last name</label>
          <mat-form-field class="signup-field">
            <input matInput placeholder="Enter your last name" formControlName="last_name" required/>
          </mat-form-field>
        </div>

        <div class="field">
          <label>Tenant / Schema name <span class="hint">(letters &amp; numbers only)</span></label>
          <mat-form-field class="signup-field">
            <input matInput placeholder="acme" formControlName="schema_name" required/>
            <mat-error
              *ngIf="
                signup.get('schema_name')?.invalid &&
                signup.get('schema_name')?.touched
              "
            >
              Invalid Tenant/Schema Name
            </mat-error>
          </mat-form-field>
        </div>

        <div class="field">
          <label>Work email</label>
          <mat-form-field class="signup-field">
            <input
              matInput
              placeholder="you@company.com"
              formControlName="email"
              required
            />
            <mat-error
              *ngIf="
                signup.get('email')?.invalid && signup.get('email')?.touched
              "
            >
              Invalid email id
            </mat-error>
          </mat-form-field>
        </div>

        <div class="field">
          <label>Password</label>
          <mat-form-field class="signup-field">
            <input matInput formControlName="password" type="password"/>
          </mat-form-field>
        </div>

        <div class="field">
          <label>Confirm password</label>
          <mat-form-field class="signup-field">
            <input
              matInput
              formControlName="confirmPassword"
              [type]="hide ? 'password' : 'text'"
            />
            <mat-icon
              mat-icon-button
              matSuffix
              (click)="hide = !hide"
              [attr.aria-label]="'Hide password'"
              [attr.aria-pressed]="hide"
            >{{ hide ? 'visibility_off' : 'visibility' }}
            </mat-icon
            >
          </mat-form-field>
          <div
            class="err"
            *ngIf="
              signup.get('confirmPassword')?.touched &&
              signup.get('confirmPassword')?.value !=
                signup.get('password')?.value
            "
          >
            Passwords do not match
          </div>
        </div>

        <button type="submit" class="btn-primary">
          Submit
        </button>
      </form>

      <div class="signup-signin">
        <span>Already have an account?</span>
        <a routerLink="../">Sign in</a>
      </div>
    </div>
  `,
  styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit {
  hide = true;
  signup = new FormGroup({
    first_name: new FormControl('', [Validators.required]),
    last_name: new FormControl('', [Validators.required]),
    schema_name: new FormControl('', [
      Validators.required,
      this.schemaNameValidator,
    ]),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
    confirmPassword: new FormControl('', Validators.required),
  });

  IsEmailVerificationRequired = false;

  constructor(
    private userServices: UserAppService,
    private snackBar: SharedFeaturesService,
    private dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.userServices.getRegistrationAllowedStatus().subscribe({
      next: (response: any) => {
        this.IsEmailVerificationRequired =
          response?.EMAIL_VERIFICATION_REQUIRED;
      },
    });
  }

  // Custom validator function
  schemaNameValidator(control: FormControl): { [key: string]: boolean } | null {
    const schemaName = control.value;
    const hasSpecialCharacters = /[\W_]/.test(schemaName);

    if (hasSpecialCharacters) {
      return {schemaNameNotAllowed: true};
    }

    return null;
  }

  onSubmit() {
    this.snackBar.openSnackBar(
      'Processing your request...',
      'Close',
      'success-snackbar'
    );
    if (this.signup.valid) {
      const values = this.signup.value;
      this.userServices.registerTenant(values).subscribe({
        next: (response: any) => {
          this.snackBar.openSnackBar(
            response.message,
            'Close',
            'success-snackbar'
          );
          const versionDialogRef = this.dialog.open(OtpVerificationComponent, {
            panelClass: 'otp-dialog',
          });
          versionDialogRef.afterClosed().subscribe((selection) => {
            if (selection) {
              this.redirect_after_delay(values);
            }
          });
        },
        error: (error: any) => {
          this.snackBar.openSnackBar(error.message, 'Close', 'error-snackbar');
        },
      });
    }
  }

  onSubmitEmailNotRequired() {
    this.snackBar.openSnackBar(
      'Processing your request...',
      'Close',
      'success-snackbar'
    );
    if (this.signup.valid) {
      const values = this.signup.value;
      this.userServices.registerTenant(values).subscribe({
        next: (response: any) => {
          this.snackBar.openSnackBar(
            response.message,
            'Close',
            'success-snackbar'
          );
          if (this.IsEmailVerificationRequired) {
            const versionDialogRef = this.dialog.open(OtpVerificationComponent, {
            panelClass: 'otp-dialog',
          });
            versionDialogRef.afterClosed().subscribe((selection) => {
              if (selection) {
                this.redirect_after_delay(values);
              }
            });
          } else {
            this.redirect_after_delay(values);
          }
        },
        error: (error: any) => {
          this.snackBar.openSnackBar(
            error.error.message,
            'Close',
            'error-snackbar'
          );
        },
      });
    }
  }

  redirect_after_delay(values: any) {
    of(null)
      .pipe(delay(2000))
      .subscribe(() => {
        this.snackBar.openSnackBar(
          'You are now being directed to your domain, where you can log in with your username and password to get started.',
          'Close',
          'success-snackbar'
        );

        of(null)
          .pipe(delay(2000))
          .subscribe(() => {
            const schemaName = values['schema_name']?.toLowerCase();
            const host = window.location.host;
            const protocol = window.location.protocol;
            const hostUrl = `${protocol}//${schemaName}.${host}`;
            const newWindow = window.open(hostUrl);
            if (newWindow === null) {
              this.snackBar.openSnackBar(
                `Please open the ${hostUrl} URL manually in your web browser, as your popup is being blocked.`,
                'Close',
                'error-snackbar'
              );
            }
          });
      });
  }
}
